import type { Podcast } from '@/types';
import { useFavorites } from '@/context/FavoritesContext';

interface PodcastCardProps {
  podcast: Podcast;
  onClick: (podcast: Podcast) => void;
}

export function PodcastCard({ podcast, onClick }: PodcastCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorite = isFavorite(podcast.id);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(podcast);
  };

  return (
    <div
      onClick={() => onClick(podcast)}
      className="glass-panel cursor-pointer group hover:border-primary/50 transition-all duration-200"
    >
      <div className="relative aspect-square mb-3 rounded-lg overflow-hidden bg-surface-50">
        {podcast.artwork || podcast.image ? (
          <img
            src={podcast.artwork || podcast.image}
            alt={podcast.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20">
            <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
        )}
        <button
          onClick={handleFavoriteClick}
          className={`absolute top-2 right-2 p-2 rounded-full backdrop-blur-sm transition-all ${
            favorite
              ? 'bg-primary/80 text-white'
              : 'bg-black/50 text-white/70 hover:text-white'
          }`}
        >
          <svg
            className="w-4 h-4"
            fill={favorite ? 'currentColor' : 'none'}
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </button>
      </div>
      <h3 className="font-medium text-white line-clamp-2 mb-1 group-hover:text-primary transition-colors">
        {podcast.title}
      </h3>
      <p className="text-sm text-white/60 line-clamp-1">
        {podcast.author || podcast.ownerName}
      </p>
      {podcast.episodeCount > 0 && (
        <p className="text-xs text-white/40 mt-1">
          {podcast.episodeCount} episodes
        </p>
      )}
    </div>
  );
}
